import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { AlertTriangle, TrendingDown } from "lucide-react";

export const UnderstandingRisks = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Understanding Risks</h1>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Important information about the risks of trading perpetual futures with leverage</p>
        </div>

        {/* Risk Warning */}
        <section className="mb-12">
          <div className="p-6 border border-red-500/20 rounded-lg bg-red-500/5">
            <div className="flex gap-3 mb-4">
              <AlertTriangle className="h-8 w-8 text-red-500 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-semibold mb-2">Important Risk Warning</h2>
                <p className="text-muted-foreground">
                  Trading perpetual futures with leverage involves significant risk of loss and is not 
                  suitable for all investors. You can lose more than your initial investment. Only trade 
                  with money you can afford to lose completely.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Leverage Risk */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Leverage Risk</h2>
          
          <p className="text-muted-foreground mb-6">
            Leverage amplifies both profits and losses. While it can multiply your gains, it can also 
            multiply your losses just as quickly.
          </p>

          <div className="p-6 border border-border rounded-lg bg-card mb-6">
            <h3 className="font-semibold mb-4">Example of Leverage Risk:</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Collateral:</span>
                <span className="font-semibold">$1,000</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Leverage:</span>
                <span className="font-semibold">20x</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Position Size:</span>
                <span className="font-semibold">$20,000</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <span className="font-semibold">5% Adverse Move:</span>
                <span className="font-bold text-red-500">100% Loss (Liquidated)</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              With 20x leverage, even a 5% move against your position results in complete loss of collateral.
            </p>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <div className="flex gap-3">
                <TrendingDown className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">Rapid Liquidation</h3>
                  <p className="text-sm text-muted-foreground">
                    High leverage positions can be liquidated within seconds during volatile market conditions. 
                    You may not have time to react or add collateral.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <div className="flex gap-3">
                <TrendingDown className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">Cascading Losses</h3>
                  <p className="text-sm text-muted-foreground">
                    If one position gets liquidated, it reduces your total equity, which can trigger 
                    liquidations on other positions if your margin ratio falls below requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Market Risk */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Market Risk</h2>
          
          <div className="space-y-4">
            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Price Volatility</h3>
              <p className="text-sm text-muted-foreground">
                Cryptocurrency markets are extremely volatile. Prices can move 10-20% or more in a single 
                day. This volatility is amplified when using leverage.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Flash Crashes & Spikes</h3>
              <p className="text-sm text-muted-foreground">
                Sudden, extreme price movements (flash crashes or spikes) can trigger mass liquidations 
                and cause losses even if the price quickly recovers. These events happen regularly in crypto.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">24/7 Markets</h3>
              <p className="text-sm text-muted-foreground">
                Crypto markets never close. Major price moves can happen at any time, including while you're 
                sleeping. This means positions require constant monitoring or risk management tools.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Low Liquidity Periods</h3>
              <p className="text-sm text-muted-foreground">
                During low liquidity periods, price slippage can be significant, and liquidation can occur 
                at worse prices than calculated.
              </p>
            </div>
          </div>
        </section>

        {/* Funding Rate Risk */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Funding Rate Risk</h2>
          
          <p className="text-muted-foreground mb-6">
            Perpetual positions incur funding payments every 8 hours. These costs can accumulate quickly:
          </p>

          <div className="p-6 border border-border rounded-lg bg-card">
            <h3 className="font-semibold mb-4">Funding Cost Example:</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Position Size:</span>
                <span className="font-semibold">$10,000</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Average Funding Rate:</span>
                <span className="font-semibold">0.01% per 8h</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Daily Cost:</span>
                <span className="font-semibold">$3</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <span className="font-semibold">Monthly Cost:</span>
                <span className="font-bold text-amber-500">$90</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Holding positions long-term can incur significant funding costs that erode profits.
            </p>
          </div>
        </section>

        {/* Platform Risk */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Platform & Technical Risk</h2>
          
          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Smart Contract Risk</h3>
              <p className="text-sm text-muted-foreground">
                While Nordex aims for security, all DeFi platforms carry smart contract risk. Bugs or 
                exploits, though rare, can result in loss of funds.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Network Congestion</h3>
              <p className="text-sm text-muted-foreground">
                During high network activity, transaction delays or failures can prevent you from closing 
                positions or adding collateral when needed.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Oracle Risks</h3>
              <p className="text-sm text-muted-foreground">
                Price feeds rely on oracles. In rare cases, oracle failures or manipulation can lead to 
                incorrect liquidations or pricing.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Interface Issues</h3>
              <p className="text-sm text-muted-foreground">
                Technical issues with the trading interface, browser, or internet connection can prevent 
                you from managing positions during critical moments.
              </p>
            </div>
          </div>
        </section>

        {/* Psychological Risk */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Psychological & Behavioral Risks</h2>
          
          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Emotional Trading</h3>
              <p className="text-sm text-muted-foreground">
                Fear and greed can lead to poor decisions. Revenge trading after losses, FOMO during pumps, 
                and panic selling during dumps are common mistakes.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Overconfidence</h3>
              <p className="text-sm text-muted-foreground">
                Early success can lead to overconfidence and taking excessive risks. Many traders blow up 
                their accounts after a winning streak by using too much leverage.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Addiction</h3>
              <p className="text-sm text-muted-foreground">
                Leveraged trading can be addictive. The constant price action, quick profits, and 24/7 
                availability can lead to compulsive trading behavior.
              </p>
            </div>
          </div>
        </section>

        {/* Risk Management */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Risk Management Strategies</h2>
          
          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Use Lower Leverage</h3>
              <p className="text-sm text-muted-foreground">
                Start with 1-3x leverage. Even experienced traders rarely use more than 10x. Higher leverage 
                doesn't mean higher profits—it just means higher risk.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Position Sizing</h3>
              <p className="text-sm text-muted-foreground">
                Never risk more than 1-2% of your account on a single trade. Use proper position sizing 
                based on your risk tolerance and stop-loss distance.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Set Stop Losses</h3>
              <p className="text-sm text-muted-foreground">
                Know your exit point before entering. Use <a href="/order-types" className="text-primary hover:underline">Take Profit and Stop Loss</a> to 
                automatically close positions at your target prices, limiting downside risk.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Diversify</h3>
              <p className="text-sm text-muted-foreground">
                Don't put all your capital in one position. Spread risk across multiple markets and 
                strategies. Keep some funds in reserve.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Education First</h3>
              <p className="text-sm text-muted-foreground">
                Never trade strategies or instruments you don't fully understand. Paper trade or use very 
                small amounts until you're consistently profitable.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Take Breaks</h3>
              <p className="text-sm text-muted-foreground">
                After significant wins or losses, step away from trading. Emotional trading leads to 
                mistakes. Set daily/weekly loss limits and stick to them.
              </p>
            </div>
          </div>
        </section>

        {/* Final Warning */}
        <section className="mb-12">
          <div className="p-6 border border-red-500/20 rounded-lg bg-red-500/5">
            <h2 className="text-xl font-semibold mb-4">Final Warning</h2>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                <strong className="text-foreground">Trading is not gambling, but with high leverage it can feel like it.</strong> Most 
                retail traders lose money, especially when using high leverage. The platforms and 
                experienced traders profit from novice mistakes.
              </p>
              <p>
                <strong className="text-foreground">Only trade with money you can afford to lose completely.</strong> Never trade with:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Money needed for living expenses</li>
                <li>Emergency funds</li>
                <li>Borrowed money or loans</li>
                <li>Money you'll need in the short term</li>
                <li>Your life savings</li>
              </ul>
              <p>
                If you're not prepared to lose the money, don't trade it.
              </p>
              <p>
                For full details, please review our <a href="/terms" className="text-primary hover:underline">Terms of Service</a>.
              </p>
            </div>
          </div>
        </section>

        <DocsNavigation 
          previous={{ title: "Wallet Security", href: "/wallet-security" }}
        />
      </div>
    </DocsLayout>
  );
};

